import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Search, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { SCHOOL, NAV_LINKS, MORE_LINKS, ALL_LINKS } from '@/data/content';
import SearchOverlay from '@/components/SearchOverlay';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  const linkCls = ({ isActive }) =>
    `relative px-1 py-2 text-sm font-semibold tracking-wide transition-colors ${
      isActive ? 'text-crimson gold-underline' : 'text-navy hover:text-crimson'
    }`;

  const moreActive = MORE_LINKS.some((l) => l.path === location.pathname);

  return (
    <>
      <header data-testid="site-header" className="sticky top-0 z-50 bg-cream/85 backdrop-blur-xl border-b border-navy/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between gap-4">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
              <img src="/images/logo.png" alt="Ideal Public School logo" className="h-12 w-12 rounded-full ring-2 ring-gold object-cover" />
              <span className="leading-tight">
                <span className="block font-display text-lg sm:text-xl font-black text-navy tracking-tight">
                  Ideal <span className="text-crimson">Public</span> School
                </span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-navy/50">{SCHOOL.place}</span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
              {NAV_LINKS.map((l) => (
                <NavLink key={l.path} to={l.path} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`} className={linkCls}>
                  {l.label}
                </NavLink>
              ))}
              <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
                <button
                  data-testid="nav-more-toggle"
                  className={`flex items-center gap-1 px-1 py-2 text-sm font-semibold tracking-wide transition-colors ${moreActive ? 'text-crimson' : 'text-navy hover:text-crimson'}`}
                  aria-expanded={moreOpen}
                >
                  More <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {moreOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full w-52 rounded-lg border border-navy/10 bg-white p-2 shadow-xl shadow-navy/10"
                    >
                      {MORE_LINKS.map((l) => (
                        <NavLink
                          key={l.path}
                          to={l.path}
                          data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                          className={({ isActive }) =>
                            `block rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? 'bg-gold-light text-navy' : 'text-navy hover:bg-cream-dark'}`
                          }
                        >
                          {l.label}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="search-toggle"
                onClick={() => setSearchOpen(true)}
                aria-label="Search the website"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors hover:border-gold hover:text-crimson"
              >
                <Search className="h-4.5 w-4.5" />
              </button>
              <Link
                to="/admissions"
                data-testid="nav-cta-admission"
                className="hidden sm:inline-flex items-center rounded-full bg-crimson px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-crimson-dark hover:-translate-y-0.5"
              >
                Admission Enquiry
              </Link>
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setOpen(!open)}
                aria-label="Toggle menu"
                className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden overflow-hidden border-t border-navy/10 bg-cream"
              aria-label="Mobile"
            >
              <div className="px-4 py-4 space-y-1">
                {ALL_LINKS.map((l) => (
                  <NavLink
                    key={l.path}
                    to={l.path}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-4 py-3 text-base font-semibold ${isActive ? 'bg-gold-light text-navy' : 'text-navy hover:bg-cream-dark'}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/admissions"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="mt-2 block rounded-full bg-crimson px-4 py-3 text-center text-base font-bold text-white"
                >
                  Admission Enquiry
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Navbar;
